import React, { useState } from 'react';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import ConfirmDialog from './ConfirmDialog';
import XpnsrPageHeader from './XpnsrPageHeader';
import { Download, ShieldAlert, Trash2 } from 'lucide-react';

const EMPTY = { ip_address: '', visitor_id: '', email: '', phone: '' };

export default function GdprDsar() {
  const { t } = useT();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [eraseOpen, setEraseOpen] = useState(false);
  const [lastSummary, setLastSummary] = useState(null);

  const buildSubject = () => {
    const s = {};
    Object.keys(EMPTY).forEach((k) => {
      const v = (form[k] || '').trim();
      if (v) s[k] = v;
    });
    return s;
  };

  const hasSubject = Object.keys(buildSubject()).length > 0;

  const handleExport = async () => {
    const subject = buildSubject();
    if (Object.keys(subject).length === 0) {
      toast.error(t('Enter at least one identifier'));
      return;
    }
    try {
      setBusy(true);
      const res = await axios.post(API + '/gdpr/dsar/export', { subject });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'dsar-export.json';
      a.click();
      URL.revokeObjectURL(url);
      setLastSummary({ kind: 'export', counts: (res.data && res.data.counts) || null });
      toast.success(t('DSAR export ready'));
    } catch (err) {
      const detail = err.response && err.response.data && err.response.data.detail;
      toast.error(detail || t('Failed to export DSAR'));
    } finally {
      setBusy(false);
    }
  };

  const handleErase = async () => {
    const subject = buildSubject();
    if (Object.keys(subject).length === 0) {
      toast.error(t('Enter at least one identifier'));
      return;
    }
    try {
      setBusy(true);
      const res = await axios.post(API + '/gdpr/dsar/erase', { subject });
      setEraseOpen(false);
      setLastSummary({ kind: 'erase', counts: res.data || null });
      toast.success(t('DSAR erasure completed'));
    } catch (err) {
      const detail = err.response && err.response.data && err.response.data.detail;
      toast.error(detail || t('Failed to erase DSAR'));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, placeholder) => (
    <div className="space-y-2">
      <Label>{t(label)}</Label>
      <Input value={form[key]} onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))} placeholder={placeholder} />
    </div>
  );

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Compliance')}
        title={t('GDPR / DSAR')}
        subtitle={t('Data-subject access (Article 15) and erasure (Article 17) by identifier.')}
      />

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>{t('Data subject')}</CardTitle>
          <CardDescription>{t('Provide at least one identifier. Matching clicks and conversions across your accessible workspaces are affected.')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('ip_address', 'IP address', '203.0.113.7')}
            {field('visitor_id', 'Visitor ID', 'v_...')}
            {field('email', 'Email', 'user@example.com')}
            {field('phone', 'Phone', '+15551234567')}
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={handleExport} disabled={busy || !hasSubject}>
              <Download className="w-4 h-4 mr-2" />{t('Export data')}
            </Button>
            <Button variant="destructive" onClick={() => setEraseOpen(true)} disabled={busy || !hasSubject}>
              <Trash2 className="w-4 h-4 mr-2" />{t('Erase data')}
            </Button>
          </div>
          <div className="flex items-start gap-2 rounded-md border border-border bg-surface-2 p-3">
            <ShieldAlert className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
            <p className="text-xs text-muted-foreground">{t('Erasure is a hard delete and cannot be undone. It requires an admin role.')}</p>
          </div>
          {lastSummary && lastSummary.counts ? (
            <div>
              <Label className="text-xs text-muted-foreground">{lastSummary.kind === 'erase' ? t('Erased') : t('Exported')}</Label>
              <pre className="mt-1 overflow-x-auto rounded border border-border bg-surface-2 p-2 font-mono text-xs">{JSON.stringify(lastSummary.counts, null, 2)}</pre>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={eraseOpen}
        onOpenChange={setEraseOpen}
        title={t('Erase subject data?')}
        description={t('This permanently deletes all matching clicks and conversions. This cannot be undone.')}
        confirmLabel={t('Erase')}
        onConfirm={handleErase}
      />
    </div>
  );
}
